import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Save, ImageIcon, ToggleLeft, ToggleRight, Search, X, Filter } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import {
  useCommonProductImages,
  useCreateCommonProductImage,
  useUpdateCommonProductImage,
  useDeleteCommonProductImage,
  type CommonProductImage,
} from '@/hooks/use-common-product-images';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from '@/stores/toast-store';

// ── Add / Edit Modal ───────────────────────────────────────────────────────

interface FormModalProps {
  image?: CommonProductImage | null;
  onSave: (data: { file?: File; name: string; keywords: string; department: string; sortOrder: number }) => void;
  onCancel: () => void;
  saving: boolean;
}

function FormModal({ image, onSave, onCancel, saving }: FormModalProps) {
  const [name, setName] = useState(image?.name ?? '');
  const [keywords, setKeywords] = useState(image?.keywords?.join(', ') ?? '');
  const [department, setDepartment] = useState(image?.department ?? '');
  const [sortOrder, setSortOrder] = useState(image?.sortOrder ?? 0);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(image?.imageUrl ?? null);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/png', 'image/jpeg'].includes(f.type)) {
      toast.error('Only PNG and JPG files are accepted');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2 MB');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function handleSubmit() {
    if (!name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!image && !file) {
      toast.error('Image file is required');
      return;
    }
    onSave({
      file: file ?? undefined,
      name: name.trim(),
      keywords: keywords.trim(),
      department: department.trim(),
      sortOrder,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-4 text-lg font-semibold text-foreground">
          {image ? 'Edit Product Image' : 'Add Product Image'}
        </h3>

        <div className="space-y-4">
          {/* Image upload */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Image</label>
            <div className="flex items-center gap-4">
              {preview ? (
                <img src={preview} alt="Preview" className="h-20 w-20 rounded-lg border border-border object-cover" />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30">
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                </div>
              )}
              <div>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted"
                >
                  {image ? 'Replace Image' : 'Choose Image'}
                </button>
                <p className="mt-1 text-xs text-muted-foreground">PNG or JPG, max 2 MB</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Product Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Whole Milk"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Keywords */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Keywords</label>
            <textarea
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              rows={2}
              placeholder="milk, whole milk, 2% milk (comma-separated)"
              className="w-full resize-none rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p className="mt-0.5 text-xs text-muted-foreground">
              POS matches product names against these keywords (case-insensitive)
            </p>
          </div>

          {/* Department */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Department (optional)</label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="e.g. Dairy"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Sort Order */}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Sort Order</label>
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              className="w-24 rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p className="mt-0.5 text-xs text-muted-foreground">
              Lower numbers match first when multiple images could match a product
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            {image ? 'Save Changes' : 'Add Image'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

const PER_PAGE = 12;

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Main Page ──────────────────────────────────────────────────────────────

export function AdminCommonProductImagesPage() {
  const { data, isLoading } = useCommonProductImages();
  const createMut = useCreateCommonProductImage();
  const updateMut = useUpdateCommonProductImage();
  const deleteMut = useDeleteCommonProductImage();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CommonProductImage | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CommonProductImage | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchInput, 250);

  const setSearchAndResetPage = useCallback((value: string) => {
    setSearchInput(value);
    setPage(1);
  }, []);

  const setDepartmentAndResetPage = useCallback((value: string) => {
    setDepartmentFilter(value);
    setPage(1);
  }, []);

  const allImages: CommonProductImage[] = data?.data ?? [];

  // Extract unique departments for filter tabs
  const departments = useMemo(() => {
    const depts = new Set<string>();
    for (const img of allImages) {
      if (img.department) depts.add(img.department);
    }
    return Array.from(depts).sort();
  }, [allImages]);

  // Filter images by search + department
  const filtered = useMemo(() => {
    let result = allImages;

    if (departmentFilter !== 'all') {
      result = result.filter((img) => img.department === departmentFilter);
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter(
        (img) =>
          img.name.toLowerCase().includes(q) ||
          (img.department ?? '').toLowerCase().includes(q) ||
          (img.keywords ?? []).some((kw) => kw.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [allImages, departmentFilter, debouncedSearch]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageImages = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  async function handleCreate(formData: {
    file?: File;
    name: string;
    keywords: string;
    department: string;
    sortOrder: number;
  }) {
    if (!formData.file) return;
    await createMut.mutateAsync({
      file: formData.file,
      name: formData.name,
      keywords: formData.keywords,
      department: formData.department || undefined,
      sortOrder: formData.sortOrder,
    });
    toast.success(`"${formData.name}" added`);
    setShowForm(false);
  }

  async function handleUpdate(formData: {
    file?: File;
    name: string;
    keywords: string;
    department: string;
    sortOrder: number;
  }) {
    if (!editing) return;
    await updateMut.mutateAsync({
      id: editing.id,
      file: formData.file,
      name: formData.name,
      keywords: formData.keywords,
      department: formData.department,
      sortOrder: formData.sortOrder,
    });
    toast.success(`"${formData.name}" updated`);
    setEditing(null);
  }

  async function handleDelete(image: CommonProductImage) {
    await deleteMut.mutateAsync(image.id);
    toast.success(`"${image.name}" deleted`);
    setConfirmDelete(null);
  }

  async function handleToggleActive(image: CommonProductImage) {
    await updateMut.mutateAsync({ id: image.id, isActive: !image.isActive });
    toast.success(`"${image.name}" ${image.isActive ? 'deactivated' : 'activated'}`);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Common Product Images"
        description="Generic product images used as fallbacks when a merchant's product has no image. POS devices match products by keyword."
      />

      {/* Toolbar: Search + Department Filter + Add */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchAndResetPage(e.target.value)}
              placeholder="Search name, keyword, department..."
              className="w-full rounded-lg border border-border py-2 pl-9 pr-8 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            {searchInput && (
              <button
                onClick={() => {
                  setSearchAndResetPage('');
                  searchRef.current?.focus();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Department filter pills */}
          {departments.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <Filter className="mr-0.5 h-3.5 w-3.5 text-muted-foreground" />
              <button
                onClick={() => setDepartmentAndResetPage('all')}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  departmentFilter === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All
              </button>
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setDepartmentAndResetPage(dept)}
                  className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                    departmentFilter === dept
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {filtered.length === allImages.length
              ? `${allImages.length} image${allImages.length !== 1 ? 's' : ''}`
              : `${filtered.length} of ${allImages.length}`}
          </span>
          <button
            onClick={() => {
              setShowForm(true);
              setEditing(null);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Image
          </button>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">Loading...</div>
      ) : allImages.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/10 text-muted-foreground">
          <ImageIcon className="mb-2 h-10 w-10" />
          <p className="text-sm">No common product images yet.</p>
          <p className="text-xs">Click "Add Image" to get started.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/10 text-muted-foreground">
          <Search className="mb-2 h-8 w-8" />
          <p className="text-sm">No images match your search.</p>
          <button
            onClick={() => {
              setSearchAndResetPage('');
              setDepartmentAndResetPage('all');
            }}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pageImages.map((img) => (
              <div
                key={img.id}
                className={`group relative rounded-xl border bg-white p-3 shadow-sm transition-shadow hover:shadow-md ${
                  img.isActive ? 'border-border' : 'border-red-200 opacity-60'
                }`}
              >
                {/* Image */}
                <div className="relative mb-3 aspect-square overflow-hidden rounded-lg bg-muted/20">
                  <img src={img.imageUrl} alt={img.name} className="h-full w-full object-cover" />
                  {!img.isActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        Inactive
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <h4 className="text-sm font-semibold text-foreground truncate">{img.name}</h4>
                {img.department && <p className="text-xs text-muted-foreground">{img.department}</p>}

                {/* Keywords */}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(img.keywords ?? []).slice(0, 4).map((kw) => (
                    <span
                      key={kw}
                      className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
                    >
                      {kw}
                    </span>
                  ))}
                  {(img.keywords ?? []).length > 4 && (
                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      +{img.keywords.length - 4}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-3 flex items-center gap-1.5 border-t border-border pt-2">
                  <button
                    onClick={() => handleToggleActive(img)}
                    title={img.isActive ? 'Deactivate' : 'Activate'}
                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                  >
                    {img.isActive ? (
                      <ToggleRight className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-3.5 w-3.5 text-red-500" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(img);
                      setShowForm(false);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmDelete(img)}
                    className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                  <span className="ml-auto text-[10px] text-muted-foreground">#{img.sortOrder}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {filtered.length > PER_PAGE && (
            <DataTablePagination
              page={safePage}
              totalPages={totalPages}
              total={filtered.length}
              perPage={PER_PAGE}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Add Modal */}
      {showForm && <FormModal onSave={handleCreate} onCancel={() => setShowForm(false)} saving={createMut.isPending} />}

      {/* Edit Modal */}
      {editing && (
        <FormModal
          image={editing}
          onSave={handleUpdate}
          onCancel={() => setEditing(null)}
          saving={updateMut.isPending}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Product Image"
        message={`Are you sure you want to delete "${confirmDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (confirmDelete) handleDelete(confirmDelete);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
